import {
  put,
  select,
  takeLatest,
  call,
  takeEvery,
  delay,
} from 'redux-saga/effects';

import {
  uploadIdentityImageApi,
  uploadAudioSampleApi,
  hasIdentityImage,
  uploadWebcamSnapshotApi,
  getWebcamSnapshotBatchSignedUrl,
  getAudioProctorSignedUrl,
  uploadAudioProctorSegmentApi,
  getIdentityDetailsApi,
} from '../api/proctor';
import ActionTypes from '../constants/ActionTypes';
import { progressStop } from '../actions';
import {
  NETWORK_ERROR,
  TIMEOUT_ERROR,
  httpStatusCodes,
} from '../constants/Http';
import {
  PROCTOR_IMAGE_UPLOAD_RETRY,
  PROCTOR_IMAGE_UPLOAD_BACK_OFF_DELAY,
  PROCTOR_AUDIO_UPLOAD_RETRY,
  PROCTOR_AUDIO_UPLOAD_BACK_OFF_DELAY,
  FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY,
  FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_DEFAULT_VALUE,
  FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_MAX_LENGTH,
  IMAGE_IDENTITY_ERROR_MESSAGE,
  PROCTOR_IMAGE_UPLOAD_STATUS,
  PROCTOR_SUCCESS_IMG_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_DROP_IMG_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_IMAGE_PROCESS_STATUS,
  PROCTOR_FAILED_IMG_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_API_FAIL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_API_SUCCESS_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_NEXT_SEQUENCE_LOCALSTORAGE_KEY,
  PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY,
} from '../constants/Proctor';

import { captureException } from '../utils/sentry';
import { errorLogger } from '../utils/trackingUtil';
import { DEAFULT_ERROR_MESSAGE } from '../constants/ErrorMessages';
import {
  getApiErrorData,
  incrementProctorCacheBasedOnKey,
  setProctoringImageStatusData,
} from '../utils/helperFunction';
import { showErrorToast } from '../actions/toast';
import {
  FAILED_SOCKET_EVENTS_SESSIONSTORAGE_DEFAULT_VALUE,
  FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY,
} from '../constants/Socket';
import { getAndUpdateWebsocketSequence, dataURItoBlob } from '../utils/utils';

function blobToDataURL(blob) {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.readAsDataURL(blob);
  });
}

export function storeFailedEvents(payload) {
  try {
    const { socketEventName, socketPayload } = payload;
    const { eventGuid } = socketPayload;
    let failedEventsValue = sessionStorage.getItem(
      FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY
    );

    failedEventsValue = failedEventsValue
      ? JSON.parse(failedEventsValue)
      : FAILED_SOCKET_EVENTS_SESSIONSTORAGE_DEFAULT_VALUE;

    failedEventsValue[eventGuid] = {
      socketEventName,
      socketPayload,
    };

    sessionStorage.setItem(
      FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY,
      JSON.stringify(failedEventsValue)
    );
  } catch (sessionStorageError) {
    errorLogger(sessionStorageError);
    captureException(sessionStorageError);
  }
}

// eslint-disable-next-line consistent-return
function* uploadWebcamSnapshotWithRetries(
  signedUrl,
  compressedBlob,
  contentType,
  eventGuid,
  retry,
  backoffDelay,
  isRetryingLocalStorageImage = false,
  sequenceRetry,
  lastCacheUploadImageCallback
) {
  if (retry > 0 && signedUrl) {
    try {
      const uploadWebcamSnapshotResponse = yield call(
        uploadWebcamSnapshotApi,
        signedUrl,
        compressedBlob,
        contentType,
        retry,
        backoffDelay
      );

      if (uploadWebcamSnapshotResponse.status === httpStatusCodes.OK) {
        const imageSequenceRetry = sequenceRetry || 0;
        setProctoringImageStatusData({
          imageSequence: eventGuid,
          status: PROCTOR_IMAGE_UPLOAD_STATUS.success,
          retry:
            imageSequenceRetry === 0 && isRetryingLocalStorageImage
              ? 1
              : imageSequenceRetry,
        });
        incrementProctorCacheBasedOnKey(
          PROCTOR_SUCCESS_IMG_COUNTER_LOCALSTORAGE_KEY
        );
        return uploadWebcamSnapshotResponse;
      }
    } catch (uploadWebcamSnapshotError) {
      // Todo:  Add guid to sentry changge string to error message
      // Too noisy
      // Refer Jira: https://doselect.atlassian.net/browse/PF-1613
      // captureException(
      //   new Error(
      //     `Received S3 signedUrl status: ${uploadWebcamSnapshotResponse.status}`,
      //     uploadWebcamSnapshotResponse
      //   )
      // );
      if (!isRetryingLocalStorageImage) yield delay(backoffDelay);

      incrementProctorCacheBasedOnKey(
        PROCTOR_FAILED_IMG_COUNTER_LOCALSTORAGE_KEY
      );
      yield call(
        uploadWebcamSnapshotWithRetries,
        signedUrl,
        compressedBlob,
        contentType,
        eventGuid,
        retry - 1,
        backoffDelay,
        isRetryingLocalStorageImage,
        sequenceRetry
      );
      const imageSequenceStatus = {
        imageSequence: eventGuid,
        status: PROCTOR_IMAGE_UPLOAD_STATUS.fail,
        retry: sequenceRetry || 0,
        reason: PROCTOR_IMAGE_PROCESS_STATUS.upload,
      };
      if (uploadWebcamSnapshotError.message === NETWORK_ERROR) {
        imageSequenceStatus.subReason =
          PROCTOR_IMAGE_PROCESS_STATUS.uploadNetworkError;
      }
      if (uploadWebcamSnapshotError.message === TIMEOUT_ERROR) {
        imageSequenceStatus.subReason =
          PROCTOR_IMAGE_PROCESS_STATUS.uploadTimeoutError;
      }

      let awsErrorDetails = '';

      // Check for 400 error with XML response
      if (
        uploadWebcamSnapshotError.response?.status === 400 &&
        uploadWebcamSnapshotError.response?.headers['content-type']?.includes(
          'application/xml'
        )
      ) {
        const xmlResponse = uploadWebcamSnapshotError.response.data;

        try {
          // Parse XML response to extract Code and Message
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xmlResponse, 'application/xml');
          const code =
            xmlDoc.getElementsByTagName('Code')[0]?.textContent ||
            'UnknownError';
          const message =
            xmlDoc.getElementsByTagName('Message')[0]?.textContent ||
            'No error message provided';
          awsErrorDetails = `${code}: ${message}`;
        } catch (xmlParseError) {
          awsErrorDetails = `Error parsing AWS XML response ${xmlParseError?.message}`;
        }
      }
      setProctoringImageStatusData(imageSequenceStatus);
      errorLogger(uploadWebcamSnapshotError, awsErrorDetails);
    } finally {
      if (lastCacheUploadImageCallback) {
        lastCacheUploadImageCallback();
      }
    }
  } else {
    const PROCTOR_FAILED_IMAGES_VALUES =
      JSON.parse(
        localStorage.getItem(FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY)
      ) || FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_DEFAULT_VALUE;
    // max store 10 else return 0
    if (
      Object.keys(PROCTOR_FAILED_IMAGES_VALUES).length ===
      FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_MAX_LENGTH
    ) {
      setProctoringImageStatusData({
        imageSequence: eventGuid,
        status: PROCTOR_IMAGE_UPLOAD_STATUS.drop,
        retry: sequenceRetry || 0,
      });
      incrementProctorCacheBasedOnKey(
        PROCTOR_DROP_IMG_COUNTER_LOCALSTORAGE_KEY
      );
      // eslint-disable-next-line consistent-return
      return;
    }

    const compressedBase64 = yield call(blobToDataURL, compressedBlob);
    PROCTOR_FAILED_IMAGES_VALUES[eventGuid] = {
      signedUrl,
      compressedBase64,
      contentType,
      eventGuid,
      retry:
        sequenceRetry !== null && (sequenceRetry || sequenceRetry === 0)
          ? +sequenceRetry + 1
          : 0,
    };
    localStorage.setItem(
      FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY,
      JSON.stringify(PROCTOR_FAILED_IMAGES_VALUES)
    );
    if (lastCacheUploadImageCallback) {
      lastCacheUploadImageCallback();
    }
  }
}

function* uploadFailedProctorImages({ payload }) {
  const {
    compressedBase64,
    contentType,
    signedUrl,
    eventGuid,
    isRetryingLocalStorageImage,
    sequenceRetry = 0,
    lastCacheUploadImageCallback,
  } = payload;
  try {
    const compressedBlobImage = dataURItoBlob(compressedBase64);
    yield call(
      uploadWebcamSnapshotWithRetries,
      signedUrl,
      compressedBlobImage,
      contentType,
      eventGuid,
      PROCTOR_IMAGE_UPLOAD_RETRY,
      PROCTOR_IMAGE_UPLOAD_BACK_OFF_DELAY,
      isRetryingLocalStorageImage,
      sequenceRetry,
      lastCacheUploadImageCallback
    );
  } catch (error) {
    errorLogger(error);
    captureException(error);
  }
}

function* emitWebcamEvent({ payload }) {
  try {
    const { eventGuid, eventData, eventName, eventCreatedAt } = payload;
    const [
      { socket },
      { assessmentId, assessmentType },
      { token },
      { offsetTime },
    ] = yield select(state => [
      state.socket,
      state.assessment,
      state.testSolutionSet,
      state.appStatus,
    ]);
    const eventSequence = getAndUpdateWebsocketSequence(assessmentId);
    const socketPayload = {
      token,
      assessmentId,
      assessmentType,
      eventGuid,
      eventData: { ...eventData, eventSequence },
      eventCreatedAt,
      currentOffsetTime: offsetTime,
    };
    if (socket?.connected && token) {
      // Todo: implement and use socket emit callback
      socket.emit(eventName, socketPayload);
    } else {
      // Store failed events in localstorage. When socket is connected then emit all these events
      storeFailedEvents({
        socketEventName: eventName,
        socketPayload,
      });
    }
  } catch (error) {
    errorLogger(error);
    captureException(error);
  }
}

// eslint-disable-next-line require-yield
function* uploadSnapshot({
  signedUrl,
  originalBase64Image,
  contentType,
  eventGuid,
  retryCounter,
}) {
  try {
    const originalBlobImage = dataURItoBlob(originalBase64Image);

    // Todo: Send else case to sentry
    if (originalBlobImage instanceof Blob) {
      try {
        yield call(
          uploadWebcamSnapshotWithRetries,
          signedUrl,
          originalBlobImage,
          contentType,
          eventGuid,
          retryCounter || PROCTOR_IMAGE_UPLOAD_RETRY,
          PROCTOR_IMAGE_UPLOAD_BACK_OFF_DELAY
        );
      } catch (imageCompressionError) {
        errorLogger(imageCompressionError);
        captureException(imageCompressionError);
      }
    }
  } catch (uploadSnapshotError) {
    errorLogger(uploadSnapshotError);
    captureException(uploadSnapshotError);
  }
}

function* disableSnapshotInterval() {
  try {
    const { snapshotInterval } = yield select(state => state.socket);
    if (snapshotInterval) {
      clearInterval(snapshotInterval);
    }
  } catch (disableSnapshotIntervalError) {
    errorLogger(disableSnapshotIntervalError);
    captureException(disableSnapshotIntervalError);
  }
}

function* uploadIdentityImage(args) {
  try {
    yield put({
      type: ActionTypes.VERIFYING_IDENTITY_IMAGE,
    });
    const { assessmentId, assessmentType } = yield select(
      state => state.assessment
    );

    yield call(uploadIdentityImageApi, {
      assessmentType,
      assessmentId,
      file: args.img,
    });
    yield put({
      type: `${ActionTypes.UPLOAD_IDENTITY_IMAGE}_SUCCESS`,
    });
    yield put({
      type: `${ActionTypes.VERIFYING_IDENTITY_IMAGE}_SUCCESS`,
    });
    yield put(progressStop());
  } catch (error) {
    // Todo: Handle internet disconnect, 4xx, 5xx
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status > 499 && error.response.status <= 599) {
        yield put({
          type: `${ActionTypes.VERIFYING_IDENTITY_IMAGE}_ERROR`,
          payload: IMAGE_IDENTITY_ERROR_MESSAGE['5xx'],
        });
      } else {
        yield put({
          type: `${ActionTypes.VERIFYING_IDENTITY_IMAGE}_ERROR`,
          payload:
            IMAGE_IDENTITY_ERROR_MESSAGE[error.response.status] ||
            DEAFULT_ERROR_MESSAGE.message,
        });
      }
    } else if (error.request) {
      yield put({
        type: `${ActionTypes.VERIFYING_IDENTITY_IMAGE}_ERROR`,
        payload: IMAGE_IDENTITY_ERROR_MESSAGE.network_error,
      });
    }
    yield put({
      type: `${ActionTypes.UPLOAD_IDENTITY_IMAGE}_FAIL`,
    });
    yield put({
      type: `${ActionTypes.VERIFYING_IDENTITY_IMAGE}_FAIL`,
    });
    errorLogger(error);
  }
}

function* checkHasIdentityImage(args) {
  try {
    const resp = yield call(hasIdentityImage, args);
    if (resp.status === httpStatusCodes.OK) {
      yield put({
        type: `${ActionTypes.HAS_IDENTITY_IMAGE}_SUCCESS`,
      });
    }
  } catch (error) {
    if (error.response) {
      const { response } = error;

      if (response.status === httpStatusCodes.NOT_FOUND) {
        yield put({
          type: `${ActionTypes.HAS_IDENTITY_IMAGE}_FAIL`,
        });
      }
      // Todo: handle 5xx in case of aws outage
    }
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(error, 'PID');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    errorLogger(error);
  }
}

function* getIdentityDetails(args) {
  try {
    const resp = yield call(getIdentityDetailsApi, args);
    if (resp.status === httpStatusCodes.OK) {
      yield put({
        type: `${ActionTypes.GET_IDENTITY_DEATILS}_SUCCESS`,
        payload: {
          identityImageUrl: resp.data?.identity_image_url,
          idProofImageUrl: resp.data?.id_card_image_url,
          audioSampleUrl: resp.data?.audio_sample_url,
        },
      });
    }
  } catch (error) {
    if (error.response) {
      const { response } = error;

      if (response.status === httpStatusCodes.NOT_FOUND) {
        yield put({
          type: `${ActionTypes.GET_IDENTITY_DEATILS}_FAIL`,
        });
      }
      // Todo: handle 5xx in case of aws outage
    }
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(error, 'PID');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    errorLogger(error);
  }
}

function* uploadIdProofImage(args) {
  try {
    yield put({
      type: ActionTypes.VERIFYING_IDPROOF_IMAGE,
    });
    const { assessmentId, assessmentType } = yield select(
      state => state.assessment
    );

    yield call(uploadIdentityImageApi, {
      assessmentType,
      assessmentId,
      file: args.img,
      type: 'identity_card_image',
    });
    yield put({
      type: `${ActionTypes.UPLOAD_IDPROOF_IMAGE}_SUCCESS`,
    });
    yield put({
      type: `${ActionTypes.VERIFYING_IDPROOF_IMAGE}_SUCCESS`,
    });
    yield put(progressStop());
  } catch (error) {
    // Todo: Handle internet disconnect, 4xx, 5xx
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status > 499 && error.response.status <= 599) {
        yield put({
          type: `${ActionTypes.VERIFYING_IDPROOF_IMAGE}_ERROR`,
          payload: IMAGE_IDENTITY_ERROR_MESSAGE['5xx'],
        });
      } else {
        yield put({
          type: `${ActionTypes.VERIFYING_IDPROOF_IMAGE}_ERROR`,
          payload:
            IMAGE_IDENTITY_ERROR_MESSAGE[error.response.status] ||
            DEAFULT_ERROR_MESSAGE.message,
        });
      }
    } else if (error.request) {
      yield put({
        type: `${ActionTypes.VERIFYING_IDPROOF_IMAGE}_ERROR`,
        payload: IMAGE_IDENTITY_ERROR_MESSAGE.network_error,
      });
    }
    yield put({
      type: `${ActionTypes.UPLOAD_IDPROOF_IMAGE}_FAIL`,
    });
    yield put({
      type: `${ActionTypes.VERIFYING_IDPROOF_IMAGE}_FAIL`,
    });
    errorLogger(error);
  }
}

function* uploadAudioSample(args) {
  try {
    const { assessmentId, assessmentType } = yield select(
      state => state.assessment
    );

    yield call(uploadAudioSampleApi, {
      assessmentType,
      assessmentId,
      file: args.audioFile,
      type: 'sample_audio',
    });

    yield put({
      type: `${ActionTypes.UPLOAD_AUDIO_SAMPLE}_SUCCESS`,
    });

    yield put(progressStop());
  } catch (error) {
    yield put(showErrorToast(DEAFULT_ERROR_MESSAGE.message));
    yield put({
      type: `${ActionTypes.UPLOAD_AUDIO_SAMPLE}_FAIL`,
    });
    errorLogger(error);
  }
}

function* updateProctoringSignedUrlsWithRetries({ payload, retryCounter }) {
  if (retryCounter > 0) {
    try {
      yield put({
        type: `${ActionTypes.UPDATE_PROCTORING_SIGNED_URLS}_INITIATED`,
      });

      const signedUrlsResponse = yield call(
        getWebcamSnapshotBatchSignedUrl,
        payload
      );
      const currentImageSequence = sessionStorage.getItem(
        PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY
      );
      yield put({
        type: ActionTypes.REMOVE_PROCTORING_SIGNED_URL,
        imageSequence: currentImageSequence,
      });

      const signedUrls = signedUrlsResponse?.data?.signed_url;
      const signedUrlLength = Object.keys(signedUrls)?.length;
      yield put({
        type: `${ActionTypes.UPDATE_PROCTORING_SIGNED_URLS}_SUCCESS`,
        signedUrls,
      });
      if (signedUrlLength > 0) {
        localStorage.setItem(
          PROCTOR_BATCHED_SIGNED_NEXT_SEQUENCE_LOCALSTORAGE_KEY,
          +payload.nextImageSequence + +signedUrlLength
        );
      }
      incrementProctorCacheBasedOnKey(
        PROCTOR_BATCHED_SIGNED_URL_API_SUCCESS_COUNTER_LOCALSTORAGE_KEY
      );
    } catch (error) {
      incrementProctorCacheBasedOnKey(
        PROCTOR_BATCHED_SIGNED_URL_API_FAIL_COUNTER_LOCALSTORAGE_KEY
      );
      yield put({
        type: `${ActionTypes.UPDATE_PROCTORING_SIGNED_URLS}_FAIL`,
      });
      yield call(
        updateProctoringSignedUrlsWithRetries,
        payload,
        retryCounter - 1
      );
      errorLogger(error, payload);
    }
  }
}

// Audio proctoring: fetch signed URL for audio segment with retries
function* getAudioProctorSignedUrlWithRetries(payload, retry, backoffDelay) {
  if (retry > 0) {
    try {
      const [{ token }, { assessmentId }] = yield select(state => [
        state.testSolutionSet,
        state.assessment,
      ]);

      const requestTransactionId =
        payload.requestTransactionId || require('uuid').v4();
      const violationTimestamp =
        payload.violationTimestamp || new Date().toISOString();
      const metadata = payload.metadata || {};
      const fileType = payload.fileType || 'webm';

      const response = yield call(getAudioProctorSignedUrl, {
        token,
        assessmentId,
        requestTransactionId,
        violationTimestamp,
        metadata,
        fileType,
      });

      yield put({
        type: `${ActionTypes.GET_AUDIO_PROCTOR_SIGNED_URL}_SUCCESS`,
        payload: {
          signedUrl: response?.data?.signed_url,
          audioSequence: response?.data?.audio_sequence,
          incidentId: payload.incidentId,
          audioBlob: payload.audioBlob,
          fileType: payload.fileType,
        },
      });
      return response;
    } catch (error) {
      yield delay(backoffDelay);
      return yield call(
        getAudioProctorSignedUrlWithRetries,
        payload,
        retry - 1,
        backoffDelay
      );
    }
  } else {
    yield put({
      type: `${ActionTypes.GET_AUDIO_PROCTOR_SIGNED_URL}_FAIL`,
      incidentId: payload.incidentId,
      error: 'Max retries exceeded',
    });
    return null;
  }
}

// Audio proctoring: fetch signed URL for audio segment
function* getAudioProctorSignedUrlSaga({ payload }) {
  yield call(
    getAudioProctorSignedUrlWithRetries,
    payload,
    PROCTOR_AUDIO_UPLOAD_RETRY,
    PROCTOR_AUDIO_UPLOAD_BACK_OFF_DELAY
  );
}

// Audio proctoring: upload audio blob to signed URL with retries
function* uploadAudioProctorSegmentWithRetries(payload, retry, backoffDelay) {
  if (retry > 0) {
    try {
      const { signedUrl, audioBlob, contentType } = payload;
      yield call(
        uploadAudioProctorSegmentApi,
        signedUrl,
        audioBlob,
        contentType
      );
      yield put({
        type: `${ActionTypes.UPLOAD_AUDIO_PROCTOR_SEGMENT}_SUCCESS`,
      });
    } catch (error) {
      yield delay(backoffDelay);
      yield call(
        uploadAudioProctorSegmentWithRetries,
        payload,
        retry - 1,
        backoffDelay
      );
      errorLogger(error, payload);
    }
  } else {
    yield put({ type: `${ActionTypes.UPLOAD_AUDIO_PROCTOR_SEGMENT}_FAIL` });
    errorLogger(error, payload);
    captureException(error);
  }
}

// Audio proctoring: upload audio blob to signed URL
function* uploadAudioProctorSegmentSaga({ payload }) {
  yield call(
    uploadAudioProctorSegmentWithRetries,
    payload,
    PROCTOR_AUDIO_UPLOAD_RETRY,
    PROCTOR_AUDIO_UPLOAD_BACK_OFF_DELAY
  );
}

// Audio proctoring: complete flow - get signed URL then upload
function* processAudioProctorIncidentSaga({ payload }) {
  try {
    const {
      incidentId,
      audioBlob,
      metadata,
      violationTimestamp,
      fileType = 'webm',
    } = payload;

    // First get signed URL
    yield put({
      type: ActionTypes.GET_AUDIO_PROCTOR_SIGNED_URL,
      payload: {
        incidentId,
        audioBlob,
        metadata,
        violationTimestamp,
        fileType,
      },
    });
  } catch (error) {
    errorLogger(error, payload);
    captureException(error);
  }
}

// Handle successful signed URL fetch and trigger upload
function* handleAudioSignedUrlSuccessSaga({ payload }) {
  try {
    const { signedUrl, audioBlob, incidentId, fileType = 'webm' } = payload;

    if (signedUrl && audioBlob) {
      // Determine content type based on file type
      const contentType = fileType === 'wav' ? 'audio/wav' : 'audio/webm';

      // Upload the audio blob
      yield put({
        type: ActionTypes.UPLOAD_AUDIO_PROCTOR_SEGMENT,
        payload: {
          signedUrl,
          audioBlob,
          contentType,
          incidentId,
        },
      });
    }
  } catch (error) {
    errorLogger(error, payload);
    captureException(error);
  }
}

export default function* loadProctorSaga() {
  yield takeEvery(ActionTypes.UPLOAD_SNAPSHOT, uploadSnapshot);
  yield takeLatest(ActionTypes.UPLOAD_IDENTITY_IMAGE, uploadIdentityImage);
  yield takeLatest(ActionTypes.HAS_IDENTITY_IMAGE, checkHasIdentityImage);
  yield takeLatest(ActionTypes.UPLOAD_IDPROOF_IMAGE, uploadIdProofImage);
  yield takeLatest(ActionTypes.UPLOAD_AUDIO_SAMPLE, uploadAudioSample);
  yield takeLatest(ActionTypes.GET_IDENTITY_DEATILS, getIdentityDetails);
  yield takeLatest(
    ActionTypes.UPLOAD_FAILED_PROCTOR_IMAGES,
    uploadFailedProctorImages
  );
  yield takeLatest(
    ActionTypes.DISABLE_SNAPSHOT_INTERVAL,
    disableSnapshotInterval
  );
  yield takeEvery(ActionTypes.EMIT_WEBCAM_EVENT, emitWebcamEvent);
  yield takeLatest(
    ActionTypes.UPDATE_PROCTORING_SIGNED_URLS,
    updateProctoringSignedUrlsWithRetries
  );
  // Audio proctoring watchers
  yield takeLatest(
    ActionTypes.GET_AUDIO_PROCTOR_SIGNED_URL,
    getAudioProctorSignedUrlSaga
  );
  yield takeEvery(
    ActionTypes.UPLOAD_AUDIO_PROCTOR_SEGMENT,
    uploadAudioProctorSegmentSaga
  );
  yield takeEvery(
    ActionTypes.PROCESS_AUDIO_PROCTOR_INCIDENT,
    processAudioProctorIncidentSaga
  );
  yield takeEvery(
    `${ActionTypes.GET_AUDIO_PROCTOR_SIGNED_URL}_SUCCESS`,
    handleAudioSignedUrlSuccessSaga
  );
}
